import type { TFunction } from "i18next";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useAuth, useCan } from "../../auth/AuthProvider";
import { CopyButton } from "../../components/CopyButton";
import { CopyField } from "../../components/CopyField";
import { ModelPicker } from "../../components/ModelPicker";
import { Icon } from "../../components/icons";
import { ConfirmDialog, Modal } from "../../components/Modal";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  cx,
  EmptyState,
  Field,
  Input,
  LoadingBlock,
  Mono,
  RoleBadge,
  Select,
} from "../../components/ui";
import { API_URL, ApiError } from "../../lib/api";
import { absoluteTime, relativeTime, utcDayMonth } from "../../lib/format";
import { inferenceBaseUrl } from "../../lib/inference";
import { formatMoney, formatUsageCost } from "../../lib/money";
import {
  useAiModels,
  useApiKeys,
  useCreateApiKey,
  useInferenceUsage,
  useRevokeApiKey,
  useRotateApiKey,
  useUpdateApiKey,
} from "../../lib/queries";
import type { ApiKey, InferenceKeySpend, MemberRole } from "../../lib/types";

const ASSIGNABLE: MemberRole[] = ["viewer", "developer", "admin"];

export function ApiKeysSection() {
  const { t } = useTranslation("settings");
  /* The role words live with the badge that renders them everywhere else. */
  const { t: roles } = useTranslation("status");
  const { currentOrg } = useAuth();
  const canManage = useCan("admin");
  const orgId = currentOrg?.id;

  /*
   * Two pages, one per card. Revoked keys are kept for good, so that list
   * only grows; it is fetched for its count and opened on demand. "Show more"
   * extends a page in place — the hook keeps the previous page while the
   * larger one loads.
   */
  const [activeLimit, setActiveLimit] = useState(PAGE);
  const [revokedLimit, setRevokedLimit] = useState(PAGE);
  const [showRevoked, setShowRevoked] = useState(false);
  const activePage = useApiKeys(orgId, canManage, { state: "active", limit: activeLimit });
  const revokedPage = useApiKeys(orgId, canManage, { state: "revoked", limit: revokedLimit });
  const isPending = activePage.isPending;
  const createKey = useCreateApiKey(orgId ?? "");
  const updateKey = useUpdateApiKey(orgId ?? "");
  const catalogue = useAiModels();
  const revokeKey = useRevokeApiKey(orgId ?? "");
  const rotateKey = useRotateApiKey(orgId ?? "");

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [role, setRole] = useState<MemberRole>("developer");
  const [expiresInDays, setExpiresInDays] = useState("");
  /*
   * The two constraints, held as the form shows them: the limit as the text
   * somebody typed (in the organization's currency, cents at most), the
   * allowlist as the picker's own null-or-list. `editing` is the key the modal
   * is about, or null when it is creating one — the same modal serves both,
   * with the role and the expiry fixed once a key exists.
   */
  const [limitText, setLimitText] = useState("");
  const [allowedModels, setAllowedModels] = useState<string[] | null>(null);
  const [editing, setEditing] = useState<ApiKey | null>(null);
  const [issued, setIssued] = useState<string | null>(null);
  /** The key a shown token is a new secret for, or null when it was just created. */
  const [issuedFor, setIssuedFor] = useState<string | null>(null);
  /**
   * Which example the success dialog shows.
   *
   * Inference is the default because it is the harder of the two to get right — a
   * base URL, a model id and a key, against a REST call that needs only the key —
   * and because the AI quickstart is what sends most people to this page.
   */
  const [issuedUse, setIssuedUse] = useState<"inference" | "platform">("inference");
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const [rotating, setRotating] = useState<ApiKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  /*
   * What each key has spent on inference, over the last 30 days.
   *
   * The column behind this has been written on every inference request since the
   * feature existed and was unreadable until recently, so "which key is spending
   * this" had no answer anywhere. This is the page where somebody acts on the
   * answer — the revoke button is right here — which is why the figure belongs
   * beside the key rather than only on the AI page.
   *
   * `viewer` can read usage, so this needs no extra permission beyond the `admin`
   * this whole section already requires.
   */
  const usage = useInferenceUsage(orgId, { days: 30 });
  const currency = usage.data?.currency ?? "USD";
  const spendByKey = new Map(
    (usage.data?.byKey ?? [])
      .filter((row): row is typeof row & { keyId: string } => row.keyId !== null)
      .map((row) => [row.keyId, row]),
  );

  if (!canManage) {
    return (
      <Card>
        <EmptyState title={t("keys.denied")} description={t("keys.deniedLine")} />
      </Card>
    );
  }

  const limitMicros = limitMicrosOf(limitText);
  const limitInvalid = limitText.trim() !== "" && limitMicros === null;
  const allowlistEmpty = allowedModels !== null && allowedModels.length === 0;
  const monthToDate = editing?.inferenceMonthToDate.costMicros ?? 0;
  const limitBelowSpend = editing !== null && limitMicros !== null && limitMicros < monthToDate;

  function openCreate() {
    setEditing(null);
    setName("");
    setRole("developer");
    setExpiresInDays("");
    setLimitText("");
    setAllowedModels(null);
    setOpen(true);
  }

  function openEdit(key: ApiKey) {
    setEditing(key);
    setName(key.name);
    setRole(key.role);
    setExpiresInDays("");
    setLimitText(
      key.inferenceMonthlyLimitMicros === null
        ? ""
        : (key.inferenceMonthlyLimitMicros / 1_000_000).toFixed(2),
    );
    setAllowedModels(key.allowedModels);
    setOpen(true);
  }

  function closeModal() {
    setOpen(false);
    setEditing(null);
    setName("");
    setExpiresInDays("");
    setLimitText("");
    setAllowedModels(null);
  }

  /**
   * Only what changed goes over the wire, so an unchanged allowlist is not
   * re-validated and the audit entry names the fields that actually moved.
   */
  async function save() {
    if (!editing) return;
    setError(null);
    const patch: Parameters<typeof updateKey.mutateAsync>[0] = { keyId: editing.id };
    if (name.trim() !== editing.name) patch.name = name.trim();
    if (limitMicros !== editing.inferenceMonthlyLimitMicros) {
      patch.inferenceMonthlyLimitMicros = limitMicros;
    }
    if (!sameList(allowedModels, editing.allowedModels)) patch.allowedModels = allowedModels;
    try {
      await updateKey.mutateAsync(patch);
      closeModal();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("keys.updateError"));
    }
  }

  async function create() {
    setError(null);
    const days = Number.parseInt(expiresInDays, 10);
    try {
      const created = await createKey.mutateAsync({
        name: name.trim(),
        role,
        ...(Number.isFinite(days) && days > 0 ? { expiresInDays: days } : {}),
        inferenceMonthlyLimitMicros: limitMicros,
        allowedModels,
      });
      setIssuedFor(null);
      setIssued(created.token);
      closeModal();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("keys.createError"));
    }
  }

  const inferenceExample = issued
    ? `curl ${inferenceBaseUrl()}/chat/completions \\
  -H "Authorization: Bearer ${issued}" \\
  -H "Content-Type: application/json" \\
  -d '{"model": "MAXLAYER_MODEL_ID", "messages": [{"role": "user", "content": "Hello"}]}'`
    : "";

  const platformExample = issued
    ? `curl ${API_URL}/v1/orgs/${orgId}/apps \\
  -H "Authorization: Bearer ${issued}"`
    : "";

  const example = issuedUse === "inference" ? inferenceExample : platformExample;

  /*
   * Active first, revoked after, rather than one list under a heading that said
   * "Active keys" while containing both. A revoked key is history — worth keeping
   * visible, because "was this key ever real" is a question that gets asked after an
   * incident — but it is not part of the set somebody is deciding about.
   */
  const active = activePage.data?.data ?? [];
  const activeTotal = activePage.data?.total ?? active.length;
  const revoked = revokedPage.data?.data ?? [];
  const revokedTotal = revokedPage.data?.total ?? revoked.length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-fg">{t("keys.title")}</h2>
          {/*
            Both jobs, named. This page described keys as being for "a script or a CI
            job" while the AI quickstart sent people here to configure inference —
            so somebody arriving from that link did not see themselves described, and
            the example they were handed on success was a call to list apps.
          */}
          <p className="mt-1 max-w-2xl text-sm text-fg-subtle">{t("keys.lead")}</p>
        </div>
        <Button onClick={openCreate}>{t("keys.new")}</Button>
      </div>

      {error && <Alert>{error}</Alert>}

      <Card>
        <CardHeader
          title={t("keys.active")}
          description={spendByKey.size > 0 ? t("keys.activeSpend") : undefined}
        />
        {isPending ? (
          <LoadingBlock />
        ) : active.length === 0 ? (
          <EmptyState title={t("keys.empty")} description={t("keys.emptyLine")} />
        ) : (
          <ul className="divide-y divide-line">
            {active.map((key) => (
              <KeyRow
                key={key.id}
                apiKey={key}
                spend={spendByKey.get(key.id)}
                currency={currency}
                onEdit={() => openEdit(key)}
                onRotate={() => setRotating(key)}
                onRevoke={() => setRevoking(key)}
              />
            ))}
          </ul>
        )}
        {activeTotal > active.length && (
          <ShowMore
            older={activeTotal - active.length}
            loading={activePage.isPlaceholderData}
            onClick={() => setActiveLimit((current) => current + PAGE)}
          />
        )}
      </Card>

      {revokedTotal > 0 && (
        <Card>
          <CardHeader
            title={t("keys.revokedCount", { count: revokedTotal })}
            description={t("keys.revokedLine")}
            action={
              <Button variant="ghost" size="sm" onClick={() => setShowRevoked((v) => !v)}>
                {showRevoked ? t("keys.hide") : t("keys.show")}
              </Button>
            }
          />
          {showRevoked && (
            <>
              <ul className="divide-y divide-line">
                {revoked.map((key) => (
                  <KeyRow
                    key={key.id}
                    apiKey={key}
                    spend={spendByKey.get(key.id)}
                    currency={currency}
                  />
                ))}
              </ul>
              {revokedTotal > revoked.length && (
                <ShowMore
                  older={revokedTotal - revoked.length}
                  loading={revokedPage.isPlaceholderData}
                  onClick={() => setRevokedLimit((current) => current + PAGE)}
                />
              )}
            </>
          )}
        </Card>
      )}

      <Modal
        open={open}
        onClose={closeModal}
        title={editing ? t("keys.edit", { name: editing.name }) : t("keys.create")}
        description={editing ? t("keys.editLine") : t("keys.createLine")}
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              {t("keys.cancel")}
            </Button>
            <Button
              onClick={() => void (editing ? save() : create())}
              disabled={limitInvalid || allowlistEmpty || name.trim() === ""}
              loading={editing ? updateKey.isPending : createKey.isPending}
            >
              {editing ? t("keys.save") : t("keys.createKey")}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {/*
            Two groups, named, because the modal grew from three fields to five and
            the two new ones answer a different question. The first group is the
            key's identity, fixed once it exists except for the name; the second is
            what it may do on inference, which changes as often as a budget does.
            Same eyebrow the sign-up form uses for its two halves.
          */}
          <div className="space-y-4">
          <p className="micro text-accent">{t("keys.group.key")}</p>
          <Field label={t("keys.name")} htmlFor="keyName">
            <Input
              id="keyName"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("keys.namePlaceholder")}
            />
          </Field>

          <Field
            label={t("keys.role")}
            htmlFor="keyRole"
            hint={
              editing
                ? t("keys.roleFixed")
                : t(`keys.roleHint.${role}` as "keys.roleHint.developer")
            }
          >
            <Select
              id="keyRole"
              value={role}
              disabled={editing !== null}
              onChange={(event) => setRole(event.target.value as MemberRole)}
            >
              {ASSIGNABLE.map((value) => (
                <option key={value} value={value}>
                  {roles(`role.${value}` as "role.developer", { defaultValue: value })}
                </option>
              ))}
            </Select>
          </Field>

          {editing === null && (
            <Field
              label={t("keys.expiry")}
              htmlFor="keyExpiry"
              hint={t("keys.expiryHint")}
            >
              <Input
                id="keyExpiry"
                type="number"
                min={1}
                max={3650}
                value={expiresInDays}
                onChange={(event) => setExpiresInDays(event.target.value)}
              />
            </Field>
          )}

          {/*
            Inference only, and said so: apps and databases bill the organization
            with no key in the loop, so a "key limit" on them would be a fiction.
            The reset is the calendar month in UTC, which is the clock the ledger
            keeps. A limit set below what the key has already spent this month is
            allowed — it just refuses from the next request — and the warning
            under the field is what stops that being a surprise.
          */}
          </div>

          <div className="space-y-4 border-t border-line pt-5">
          <p className="micro text-accent">{t("keys.group.inference")}</p>
          <Field
            label={t("keys.limit")}
            htmlFor="keyLimit"
            hint={
              limitBelowSpend
                ? t("keys.limitBelow", {
                    spent: formatMoney(monthToDate, currency),
                    day: resetDay(editing?.inferenceMonthToDate.resetsAt, t),
                  })
                : t("keys.limitHint")
            }
            error={limitInvalid ? t("keys.limitInvalid") : null}
          >
            {/*
              The currency sits in the field rather than in the hint, so the number
              somebody types is read as money as they type it. The presets are the
              three budgets people actually set — a trial, a tool, a service — and
              they fill the field rather than submit it, so the number is still
              theirs to change.
            */}
            <div className="relative">
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 left-3 flex items-center font-mono text-[13px] text-fg-subtle"
              >
                {currencySymbol(currency)}
              </span>
              <Input
                id="keyLimit"
                inputMode="decimal"
                placeholder={t("keys.noLimit")}
                value={limitText}
                onChange={(event) => setLimitText(event.target.value)}
                className="pl-7"
              />
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {LIMIT_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setLimitText(String(preset))}
                  className={cx(
                    "rounded-md border px-2 py-0.5 font-mono text-[11px] transition-colors duration-200 ease-smooth",
                    limitText === String(preset)
                      ? "border-accent/60 bg-accent-soft text-fg"
                      : "border-line text-fg-muted hover:border-line-strong hover:text-fg",
                  )}
                >
                  {currencySymbol(currency)}
                  {preset}
                </button>
              ))}
              {limitText !== "" && (
                <button
                  type="button"
                  onClick={() => setLimitText("")}
                  className="rounded-md px-2 py-0.5 text-[11px] text-fg-subtle transition-colors duration-200 ease-smooth hover:text-fg"
                >
                  {t("keys.noLimit")}
                </button>
              )}
            </div>
          </Field>

          <Field label={t("keys.models")} htmlFor="keyModels">
            <ModelPicker
              id="keyModels"
              models={catalogue.data ?? []}
              value={allowedModels}
              onChange={setAllowedModels}
            />
          </Field>
          </div>
        </div>
      </Modal>

      <Modal
        open={issued !== null}
        onClose={() => setIssued(null)}
        title={issuedFor ? t("keys.rotated", { name: issuedFor }) : t("keys.issued")}
        description={issuedFor ? t("keys.rotatedLine") : t("keys.issuedLine")}
        footer={<Button onClick={() => setIssued(null)}>{t("keys.done")}</Button>}
      >
        {issued && (
          <div className="space-y-4">
            <CopyField value={issued} label={t("keys.keyField")} />
            <div>
              {/*
                Two examples, because the key does two jobs and the dialog used to
                assume one — it handed everybody a call that lists apps, including the
                people who arrived from the AI quickstart to configure a coding tool.
                Inference leads: it is the harder of the two to get right, needing a
                base URL and a model id where the platform call needs only the key.
              */}
              <div className="mb-1.5 flex items-center justify-between gap-3">
                <p className="text-xs font-medium tracking-wide text-fg-subtle uppercase">
                  {t("keys.useIt")}
                </p>
                <div className="flex gap-1">
                  {(["inference", "platform"] as const).map((use) => (
                    <button
                      key={use}
                      type="button"
                      aria-pressed={issuedUse === use}
                      onClick={() => setIssuedUse(use)}
                      className={cx(
                        "rounded-md px-2.5 py-1 text-xs font-medium transition-colors duration-200 ease-smooth",
                        issuedUse === use
                          ? "bg-raised text-fg"
                          : "text-fg-subtle hover:text-fg",
                      )}
                    >
                      {use === "inference" ? t("keys.inference") : t("keys.platform")}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-start gap-2 rounded-md border border-line bg-canvas p-3">
                <pre className="min-w-0 flex-1 overflow-x-auto font-mono text-xs leading-5 text-fg">
                  {example}
                </pre>
                <CopyButton value={example} label={t("keys.copyExample")} />
              </div>
              {issuedUse === "inference" && (
                <p className="mt-2 text-xs leading-5 text-fg-subtle">
                  <Trans
                    i18nKey="keys.replace"
                    ns="settings"
                    components={{
                      code: <Mono />,
                      catalogue: (
                        <Link
                          to="/ai/models"
                          className="text-accent underline underline-offset-2"
                        />
                      ),
                    }}
                  />
                </p>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/*
        Danger, like revoking: the secret in use stops on its next request, so
        anything holding it breaks until somebody hands it the new one. What
        follows is the same reveal-once dialog a new key gets.
      */}
      <ConfirmDialog
        open={rotating !== null}
        onClose={() => setRotating(null)}
        title={
          rotating ? t("keys.rotateTitle", { name: rotating.name }) : t("keys.rotateFallback")
        }
        description={t("keys.rotateLine")}
        confirmLabel={t("keys.rotateConfirm")}
        loading={rotateKey.isPending && rotateKey.variables === rotating?.id}
        onConfirm={() => {
          if (!rotating) return;
          setError(null);
          rotateKey.mutate(rotating.id, {
            onSuccess: (rotated) => {
              setIssuedFor(rotated.name);
              setIssued(rotated.token);
              setRotating(null);
            },
            onError: (cause) => {
              setError(cause instanceof ApiError ? cause.message : t("keys.rotateError"));
              setRotating(null);
            },
          });
        }}
      />

      <ConfirmDialog
        open={revoking !== null}
        onClose={() => setRevoking(null)}
        title={
          revoking ? t("keys.revokeTitle", { name: revoking.name }) : t("keys.revokeFallback")
        }
        description={t("keys.revokeLine")}
        confirmLabel={t("keys.revokeConfirm")}
        loading={revokeKey.isPending && revokeKey.variables === revoking?.id}
        onConfirm={() => {
          if (!revoking) return;
          setError(null);
          revokeKey.mutate(revoking.id, {
            onSuccess: () => setRevoking(null),
            onError: (cause) => {
              setError(cause instanceof ApiError ? cause.message : t("keys.revokeError"));
              setRevoking(null);
            },
          });
        }}
      />
    </div>
  );
}

function cxRow(revoked: boolean) {
  return cx(
    "flex flex-wrap items-center gap-3 px-5 py-4",
    revoked && "opacity-60",
  );
}

/**
 * One key, and what it has spent.
 *
 * The spend figure is the reason this row exists as a component rather than inline
 * markup: it is the only place in the product where "which key is burning credit"
 * and "revoke this key" are the same glance. The AI page can report the same
 * breakdown, but it cannot act on it.
 *
 * A key with no inference spend shows nothing rather than a zero. Most keys are for
 * deploys and will never call inference, and a column of `$0.00` down a list of
 * CI credentials reads as a measurement failure rather than as an absence.
 */
function KeyRow({
  apiKey,
  spend,
  currency,
  onEdit,
  onRotate,
  onRevoke,
}: {
  apiKey: ApiKey;
  spend: InferenceKeySpend | undefined;
  currency: string;
  onEdit?: () => void;
  onRotate?: () => void;
  onRevoke?: () => void;
}) {
  const { t } = useTranslation("settings");
  const expired = apiKey.expiresAt !== null && Date.parse(apiKey.expiresAt) < Date.now();

  return (
    <li className={cxRow(apiKey.revokedAt !== null)}>
      <div className="flex min-w-0 flex-1 items-center gap-3 max-sm:basis-full">
        <Icon name="key" className="size-4 shrink-0 text-fg-subtle" />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-fg">
            {apiKey.name}
            {apiKey.revokedAt && (
              <span className="ml-2 text-xs font-normal text-danger">
                {t("keys.badge.revoked")}
              </span>
            )}
            {/* Expired and revoked are different endings, and only one of them was
                somebody's decision. */}
            {!apiKey.revokedAt && expired && (
              <span className="ml-2 text-xs font-normal text-warn">
                {t("keys.badge.expired")}
              </span>
            )}
            {/* A key that has never been used is either a mistake or a leak waiting
                to happen. Worth saying, because it is invisible otherwise — a null
                last-used renders as a dash beside three real dates. */}
            {!apiKey.revokedAt && apiKey.lastUsedAt === null && (
              <span className="ml-2 text-xs font-normal text-fg-subtle">
                {t("keys.badge.neverUsed")}
              </span>
            )}
            {/* The meter below says how much; this says whether it matters, in the
                place a name is read. "At limit" is a key that is refusing requests
                right now, which somebody scanning for why a tool stopped needs to
                see without reading a number. */}
            {!apiKey.revokedAt && limitState(apiKey) === "at" && (
              <span className="ml-2 text-xs font-normal text-danger">
                {t("keys.badge.at")}
              </span>
            )}
            {!apiKey.revokedAt && limitState(apiKey) === "near" && (
              <span className="ml-2 text-xs font-normal text-warn">
                {t("keys.badge.near")}
              </span>
            )}
          </p>
          <p className="mt-0.5 truncate text-xs text-fg-subtle">
            <span className="font-mono">mxl_{apiKey.prefix}…</span>
            <span className="mx-1.5">·</span>
            {t("keys.created", { at: absoluteTime(apiKey.createdAt) })}
            {apiKey.lastUsedAt !== null && (
              <>
                <span className="mx-1.5">·</span>
                {t("keys.lastUsed", { at: relativeTime(apiKey.lastUsedAt) })}
              </>
            )}
            {apiKey.expiresAt && (
              <>
                <span className="mx-1.5">·</span>
                {t(expired ? "keys.expiredAt" : "keys.expires", {
                  at: absoluteTime(apiKey.expiresAt),
                })}
              </>
            )}
          </p>
          <LimitStatus apiKey={apiKey} currency={currency} />
          {spend && (
            <p className="mt-1 text-xs text-fg-muted">
              {/* Token spend, so it gets the sub-cent formatter rather than the
                  cents one every balance on the billing page uses. A key that
                  ran a light month read "$0.01 on inference over 30 days" beside
                  a request count in the hundreds. */}
              <Trans
                i18nKey="keys.spend"
                ns="settings"
                values={{ amount: formatUsageCost(spend.costMicros, currency) }}
                components={{ figure: <span className="font-mono tabular-nums" /> }}
              />
              <span className="mx-1.5 text-fg-subtle">·</span>
              <Trans
                i18nKey="keys.requests"
                ns="settings"
                count={spend.requests}
                values={{ count: spend.requests.toLocaleString() }}
                components={{ figure: <span className="font-mono tabular-nums" /> }}
              />
            </p>
          )}
        </div>
      </div>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        <RoleBadge role={apiKey.role} />
        {onEdit && (
          <Button variant="ghost" size="sm" onClick={onEdit}>
            {t("keys.editAction")}
          </Button>
        )}
        {/* Not on an expired key, which would come back expired: a new key is
            what that wants, and the API refuses the rotation. */}
        {onRotate && !expired && (
          <Button variant="ghost" size="sm" onClick={onRotate}>
            {t("keys.rotateAction")}
          </Button>
        )}
        {onRevoke && (
          <Button variant="ghost" size="sm" onClick={onRevoke}>
            {t("keys.revokeAction")}
          </Button>
        )}
      </div>
    </li>
  );
}

/**
 * The limit field's text as micros, or null when it is blank or not a money
 * amount. Two decimals at most, like the top-up field on the billing page —
 * and not imported from there, because a page importing another page's
 * helper is the kind of dependency that survives until somebody deletes the
 * wrong file.
 */
function limitMicrosOf(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const micros = Math.round(Number(trimmed) * 1_000_000);
  return micros >= 10_000 ? micros : null;
}

function sameList(a: string[] | null, b: string[] | null): boolean {
  if (a === null || b === null) return a === b;
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

/** "1 Oct", from the ISO instant the API sends. */
function resetDay(iso: string | undefined, t: TFunction<"settings">): string {
  return iso ? utcDayMonth(iso) : t("keys.firstOfMonth");
}

/**
 * What a key may spend and call, on the row — a meter for the limit and a
 * count for the allowlist. A key with neither shows nothing: most keys are
 * for deploys, and a row of "no limit · any model" down a list of CI
 * credentials says nothing anybody came to read.
 *
 * The meter reads month-to-date from the usage rows, not from the counter
 * the API gates on, which is why a limit set mid-month can show more spent
 * than it will refuse on — the hint on the field says so.
 */
function LimitStatus({ apiKey, currency }: { apiKey: ApiKey; currency: string }) {
  const { t } = useTranslation("settings");
  const limit = apiKey.inferenceMonthlyLimitMicros;
  const models = apiKey.allowedModels;
  if (limit === null && models === null) return null;
  const spent = apiKey.inferenceMonthToDate.costMicros;
  const fraction = limit === null ? 0 : Math.min(1, spent / limit);
  const tone = fraction >= 1 ? "bg-danger" : fraction >= 0.8 ? "bg-warn" : "bg-accent";
  return (
    <div className="mt-1.5 space-y-1 text-xs text-fg-muted">
      {limit !== null && (
        <div className="max-w-xs">
          <div className="flex items-baseline justify-between gap-3">
            <span>
              <Trans
                i18nKey="keys.meter"
                ns="settings"
                values={{
                  spent: formatMoney(spent, currency),
                  limit: formatMoney(limit, currency),
                }}
                components={{
                  figure: (
                    <span
                      className={cx("font-mono tabular-nums", fraction >= 1 && "text-danger")}
                    />
                  ),
                }}
              />
            </span>
            <span className="shrink-0 text-fg-subtle">
              {t("keys.resets", { day: resetDay(apiKey.inferenceMonthToDate.resetsAt, t) })}
            </span>
          </div>
          <div className="mt-1 h-0.5 w-full bg-line" aria-hidden="true">
            <div className={cx("h-0.5", tone)} style={{ width: `${fraction * 100}%` }} />
          </div>
        </div>
      )}
      {models !== null && (
        <p>
          <Trans
            i18nKey="keys.restricted"
            ns="settings"
            count={models.length}
            components={{ figure: <span className="font-mono tabular-nums text-fg" /> }}
          />
          <span className="mx-1.5 text-fg-subtle">·</span>
          <span className="font-mono text-fg-subtle" title={models.join(", ")}>
            {models.slice(0, 2).join(", ")}
            {models.length > 2 ? ", …" : ""}
          </span>
        </p>
      )}
    </div>
  );
}

/** Budgets people actually set: a trial, a tool, a service. In the org's currency units. */
const LIMIT_PRESETS = [10, 50, 200] as const;

/** How many keys to fetch at a time, and how many more each "Show more". */
const PAGE = 25;

/** The same footer the apps and databases lists grow by. */
function ShowMore({
  older,
  loading,
  onClick,
}: {
  older: number;
  loading: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation("settings");
  return (
    <div className="border-t border-line p-3">
      <Button variant="secondary" size="sm" className="w-full" loading={loading} onClick={onClick}>
        {t("keys.showMore", { count: older })}
      </Button>
    </div>
  );
}

function currencySymbol(currency: string): string {
  return currency === "USD" ? "$" : currency === "EUR" ? "€" : currency === "GBP" ? "£" : `${currency} `;
}

/** Where a capped key stands this month, for the badge beside its name. */
function limitState(apiKey: ApiKey): "at" | "near" | "under" | "none" {
  const limit = apiKey.inferenceMonthlyLimitMicros;
  if (limit === null) return "none";
  const fraction = apiKey.inferenceMonthToDate.costMicros / limit;
  return fraction >= 1 ? "at" : fraction >= 0.8 ? "near" : "under";
}
